import L from 'leaflet'

// Interface to represent serialized layer data
interface PersistedLayerInfo {
  id: string
  bounds: {
    _southWest: { lat: number; lng: number }
    _northEast: { lat: number; lng: number }
  }
  isVisible: boolean
  gsd?: number
  metadata?: {
    width: number
    height: number
    samplesPerPixel: number
    bitsPerSample: number[] | number
    photometric: number
  }
  imageUrl?: string
  thumbnail?: string
  predictions?: any[]
}

// Interface for layer info consumed by the component
export interface LayerInfo {
  id: string
  bounds: L.LatLngBounds
  overlay: L.ImageOverlay
  thumbnail?: string
  isVisible: boolean
  gsd?: number
  metadata?: {
    width: number
    height: number
    samplesPerPixel: number
    bitsPerSample: number[] | number
    photometric: number
  }
  imageUrl?: string
  isProcessing?: boolean
  originalFile?: File
  predictions?: any[]
  predictionRectangles?: L.Rectangle[]
}

// Storage constants
const STORAGE_KEY_PREFIX = 'autovision_map_layer_'
const STORAGE_IDS_KEY = 'autovision_map_layer_ids'
const MAX_STORAGE_SIZE = 4 * 1024 * 1024 // 4MB limit

// Check available storage space
const getStorageSize = (): number => {
  let totalSize = 0
  for (const key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      totalSize += (localStorage[key].length + key.length) * 2
    }
  }
  return totalSize
}

// Process an image for storage (resize & compress)
export const processImageForStorage = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      // Create canvas with original image dimensions (no resizing)
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Draw image at full resolution
      ctx.drawImage(img, 0, 0, img.width, img.height)

      // Use PNG format to preserve full quality
      const dataUrl = canvas.toDataURL('image/png')
      resolve(dataUrl)
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    // Handle both URL and data URL sources
    if (imageUrl.startsWith('data:')) {
      img.src = imageUrl
    } else {
      fetch(imageUrl)
        .then((res) => res.blob())
        .then((blob) => {
          img.src = URL.createObjectURL(blob)
        })
        .catch((err) => {
          reject(err)
        })
    }
  })
}

// Create a thumbnail from an image
export const createThumbnail = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 100
      canvas.height = 100

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0, 100, 100)
      const thumbnailUrl = canvas.toDataURL('image/png')
      resolve(thumbnailUrl)
    }

    img.onerror = () => {
      reject(new Error('Failed to load image for thumbnail'))
    }

    // Handle both URL and data URL sources
    if (imageUrl.startsWith('data:')) {
      img.src = imageUrl
    } else {
      img.src = imageUrl
    }
  })
}

// Helper function to recreate prediction rectangles
const createPredictionRectangles = (map: L.Map, layer: LayerInfo): L.Rectangle[] => {
  if (!layer.predictions || !layer.metadata) {
    return []
  }

  const rectangles: L.Rectangle[] = []
  const imageWidth = layer.metadata.width
  const imageHeight = layer.metadata.height
  const layerBounds = layer.bounds

  layer.predictions.forEach((prediction) => {
    const { xmin, ymin, xmax, ymax } = prediction.bounding_boxes

    // Convert pixel coordinates to latLng coordinates
    const southWest = layerBounds.getSouthWest()
    const northEast = layerBounds.getNorthEast()
    const lngDiff = northEast.lng - southWest.lng
    const latDiff = northEast.lat - southWest.lat

    // Calculate the proportional position in the image
    const proportionalXmin = xmin / imageWidth
    const proportionalXmax = xmax / imageWidth
    const proportionalYmin = 1 - ymin / imageHeight // Invert Y axis
    const proportionalYmax = 1 - ymax / imageHeight // Invert Y axis

    // Convert to map coordinates
    const rectSouthWest = L.latLng(
      southWest.lat + latDiff * proportionalYmax,
      southWest.lng + lngDiff * proportionalXmin
    )

    const rectNorthEast = L.latLng(
      southWest.lat + latDiff * proportionalYmin,
      southWest.lng + lngDiff * proportionalXmax
    )

    // Create the rectangle bounds
    const rectBounds = L.latLngBounds(rectSouthWest, rectNorthEast)

    // Get color based on class name
    const getClassColor = (className: string): string => {
      const colorMap: Record<string, string> = {
        combatant: '#FF5733',
        cargo: '#33FF57',
        tanker: '#3357FF',
        fishing: '#FF33A8',
        passenger: '#33FFF5',
        tug: '#F533FF',
        default: '#FFFF33',
      }
      return colorMap[className] || colorMap['default']
    }

    // Create rectangle with class-specific styling
    const rectangle = L.rectangle(rectBounds, {
      color: getClassColor(prediction.class_name),
      weight: 2,
      opacity: 0.8,
      fillOpacity: 0.2,
      fillColor: getClassColor(prediction.class_name),
      className: `prediction-box ${prediction.class_name}`,
    })

    // Add tooltip with prediction info
    rectangle.bindTooltip(
      `<div class="prediction-tooltip">
    <strong>${prediction.class_name}</strong>
    <div>Confidence: ${(prediction.confidence * 100).toFixed(2)}%</div>
  </div>`,
      { sticky: true, className: 'prediction-tooltip' }
    )

    // Add the rectangle to the map if layer is visible
    if (layer.isVisible) {
      rectangle.addTo(map)
    }

    rectangles.push(rectangle)
  })

  return rectangles
}

// Save a layer to localStorage
export const saveLayer = async (layer: LayerInfo): Promise<boolean> => {
  try {
    if (!layer.imageUrl) {
      console.error('Cannot save layer without imageUrl')
      return false
    }

    // Check storage size
    if (getStorageSize() > MAX_STORAGE_SIZE) {
      console.warn('Storage limit reached, clearing old layers')
      await clearAllLayers()
    }

    // Prepare layer for storage
    const persistedLayer: PersistedLayerInfo = {
      id: layer.id,
      bounds: {
        _southWest: {
          lat: layer.bounds.getSouthWest().lat,
          lng: layer.bounds.getSouthWest().lng,
        },
        _northEast: {
          lat: layer.bounds.getNorthEast().lat,
          lng: layer.bounds.getNorthEast().lng,
        },
      },
      isVisible: layer.isVisible,
      gsd: layer.gsd,
      metadata: layer.metadata,
      imageUrl: layer.imageUrl,
      thumbnail: layer.thumbnail,
      predictions: layer.predictions,
    }

    // Save to localStorage
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${layer.id}`, JSON.stringify(persistedLayer))

    // Update layer IDs
    const existingIds = JSON.parse(localStorage.getItem(STORAGE_IDS_KEY) || '[]')
    if (!existingIds.includes(layer.id)) {
      existingIds.push(layer.id)
      localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify(existingIds))
    }

    console.log(`Saved layer ${layer.id} to localStorage`)
    return true
  } catch (error) {
    console.error('Error saving layer to localStorage:', error)
    return false
  }
}

// Save multiple layers
export const saveLayers = async (layers: LayerInfo[]): Promise<boolean> => {
  try {
    // Save layer IDs
    const layerIds = layers.map((layer) => layer.id)
    localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify(layerIds))

    // Save each layer
    for (const layer of layers) {
      await saveLayer(layer)
    }

    return true
  } catch (error) {
    console.error('Failed to save layers:', error)
    return false
  }
}

// Load a single layer
export const loadLayer = (layerId: string, map: L.Map): LayerInfo | null => {
  try {
    const serializedLayer = localStorage.getItem(`${STORAGE_KEY_PREFIX}${layerId}`)
    if (!serializedLayer) {
      console.log(`No saved data for layer ${layerId}`)
      return null
    }

    const persistedLayer: PersistedLayerInfo = JSON.parse(serializedLayer)
    if (!persistedLayer.imageUrl) {
      console.log(`Layer ${layerId} has no image URL`)
      return null
    }

    // Recreate the layer
    const bounds = L.latLngBounds(
      L.latLng(persistedLayer.bounds._southWest.lat, persistedLayer.bounds._southWest.lng),
      L.latLng(persistedLayer.bounds._northEast.lat, persistedLayer.bounds._northEast.lng)
    )

    const overlay = L.imageOverlay(persistedLayer.imageUrl, bounds, {
      opacity: 1,
      interactive: true,
      crossOrigin: 'anonymous',
    })

    if (persistedLayer.isVisible) {
      overlay.addTo(map)
    }

    // Create a layer object with recovered data
    const layer: LayerInfo = {
      id: persistedLayer.id,
      bounds,
      overlay,
      isVisible: persistedLayer.isVisible,
      gsd: persistedLayer.gsd,
      metadata: persistedLayer.metadata,
      imageUrl: persistedLayer.imageUrl,
      thumbnail: persistedLayer.thumbnail,
      predictions: persistedLayer.predictions,
    }

    // If predictions exist, recreate prediction rectangles
    if (layer.predictions && layer.predictions.length > 0) {
      layer.predictionRectangles = createPredictionRectangles(map, layer)
    }

    return layer
  } catch (error) {
    console.error(`Error loading layer ${layerId}:`, error)
    return null
  }
}

// Load all saved layers
export const loadLayers = (map: L.Map): Promise<LayerInfo[]> => {
  return new Promise((resolve, reject) => {
    try {
      const savedIds = localStorage.getItem(STORAGE_IDS_KEY)
      if (!savedIds) {
        console.log('No saved layer IDs found')
        resolve([])
        return
      }

      const layerIds: string[] = JSON.parse(savedIds)
      console.log(`Found ${layerIds.length} saved layers`)

      const layers: LayerInfo[] = []

      // Load each layer
      for (const id of layerIds) {
        const layer = loadLayer(id, map)
        if (layer) {
          layers.push(layer)
        }
      }

      resolve(layers)
    } catch (error) {
      console.error('Error loading layers:', error)
      reject(error)
    }
  })
}

// Delete a layer
export const deleteLayer = async (layerId: string): Promise<boolean> => {
  try {
    // Remove the layer data
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${layerId}`)

    // Update ID list
    const layerIds = JSON.parse(localStorage.getItem(STORAGE_IDS_KEY) || '[]')
    const updatedIds = layerIds.filter((id: string) => id !== layerId)
    localStorage.setItem(STORAGE_IDS_KEY, JSON.stringify(updatedIds))

    return true
  } catch (error) {
    console.error(`Error deleting layer ${layerId}:`, error)
    return false
  }
}

// Clear all layers
export const clearAllLayers = async (): Promise<boolean> => {
  try {
    const layerIds = JSON.parse(localStorage.getItem(STORAGE_IDS_KEY) || '[]')

    // Remove each layer
    for (const id of layerIds) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${id}`)
    }

    // Clear the ID list
    localStorage.removeItem(STORAGE_IDS_KEY)
    return true
  } catch (error) {
    console.error('Error clearing layers:', error)
    return false
  }
}
